import React, { useState, useEffect } from "react";
import Breadcrumbs from "../../Components/Common/breadcrumbs";
import DashboardCard from "./DashboardCard";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import ShoppingBag from "@mui/icons-material/ShoppingBag";
import Visibility from "@mui/icons-material/Visibility";
import WhatsApp from "@mui/icons-material/WhatsApp";
import AttachMoney from "@mui/icons-material/AttachMoney";
import QuickActions from "./QuickAction";
import RecentOrdersCard, { Order } from "./RecentOrdersCard";
import StorePerformanceCard from "./StorePerformanceCard";
import Axios from "../../Common/api/axiosConfig";
import { StoreData } from "../../Components/Common/breadcrumbs";
import { onActiveStoreChange } from "../../utils/storeHelper"; // ✅ import type

const Dashboard: React.FC = () => {
  document.title = "Dashboard | Rudrix";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [dashboardData, setDashboardData] = useState({
    totalProducts: 0,
    storeViews: 0,
    orders: 0,
    revenue: 0,
  });
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [storeData, setStoreData] = useState<StoreData | null>(null); // ✅ added
  const [fetchLoading, setFetchLoading] = useState(false); // ✅ added
  const [error, setError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false); // ✅ added
  const [loading, setLoading] = useState(true);

  // ✅ Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await Axios.get("/dashboard/");
      if (response.data.success) {
        const { DashboardData, totalProducts, recentOrdersStats } =
          response.data.data;

        setDashboardData({
          totalProducts: totalProducts || 0,
          storeViews: DashboardData?.store_view || 0,
          orders: DashboardData?.orders || 0,
          revenue: DashboardData?.revenue || 0,
        });
        setRecentOrders(recentOrdersStats || []);
      }
    } catch (error: any) {
      console.error("Error fetching dashboard data:", error);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch store data (centralized)
  const fetchStoreData = async () => {
    try {
      setFetchLoading(true);
      const userDataString = localStorage.getItem("userData");

      if (!userDataString) {
        setError("User data not found. Please login again.");
        setOpenSnackbar(true);
        return;
      }

      const userData = JSON.parse(userDataString);
      const userId = userData._id || userData.id;

      if (!userId) {
        setError("User ID not found in user data.");
        setOpenSnackbar(true);
        return;
      }

      const response = await Axios.get(`/store/${userId}`);
      const store = response.data?.store;
      console.log("Fetched store data:", store);
      setStoreData(store);
    } catch (error: any) {
      console.error("Error fetching store data:", error);
      setError("Failed to load store data");
      setOpenSnackbar(true);
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchStoreData(); // ✅ call here

    const unsubscribe = onActiveStoreChange(() => {
      fetchDashboardData();
      fetchStoreData();
    });
    return () => unsubscribe();
  }, []);

  const formatNumber = (num: number) =>
    num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const formatCurrency = (amount: number) => `₹${formatNumber(amount)}`;

  const cardData = [
    {
      title: "Total Products",
      value: loading ? "Loading..." : formatNumber(dashboardData.totalProducts),
      icon: <ShoppingBag />,
      trendValue: `${dashboardData.totalProducts} available`,
    },
    {
      title: "Store Views",
      value: loading ? "Loading..." : formatNumber(dashboardData.storeViews),
      icon: <Visibility />,
      trendValue: "Total store visits",
    },
    {
      title: "WhatsApp Orders",
      value: loading ? "Loading..." : formatNumber(dashboardData.orders),
      icon: <WhatsApp />,
      trendValue: "via WhatsApp messages",
    },
    {
      title: "Revenue",
      value: loading ? "Loading..." : formatCurrency(dashboardData.revenue),
      icon: <AttachMoney />,
      trendValue: "Total revenue",
    },
  ];

  const storePerformanceData = {
    "Store Views": dashboardData.storeViews,
    "WhatsApp Clicks": dashboardData.orders,
    "Total Orders": dashboardData.orders,
    "Total Revenue": formatCurrency(dashboardData.revenue),
  };

  return (
    <>
      <Breadcrumbs />

      {error && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: "#ffebee",
            color: "#c62828",
            borderRadius: 1,
          }}
        >
          {error}
        </Box>
      )}

      <Grid container spacing={2} sx={{ mt: 3 }}>
        {cardData.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <DashboardCard {...card} />
          </Grid>
        ))}
      </Grid>

      {/* ✅ Pass storeData + fetchStoreData to QuickActions */}
      <Box sx={{ mb: 4, mt: 4 }}>
        <QuickActions
          storeData={storeData}
          fetchStoreData={fetchStoreData}
          setError={setError}
          setOpenSnackbar={setOpenSnackbar}
        />
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <RecentOrdersCard orders={recentOrders} />
        </Grid>
        <Grid item xs={12} md={6}>
          <StorePerformanceCard performanceData={storePerformanceData} />
        </Grid>
      </Grid>

      {isMobile && <Box sx={{ height: 50 }} />}
    </>
  );
};

export default Dashboard;
